import { ServerErrorField } from "@/components/server-error-field/ServerErrorField.tsx";
import {
  WebhookHeaderEditModal,
  WebhookHeaderValue,
} from "@/pages/settings/webhooks/widgets/WebhookHeaderEditModal.tsx";
import { WebhookSecretModal } from "@/pages/settings/webhooks/widgets/WebhookSecretModal.tsx";
import { useWebhookSave } from "@/queries/settings/use-webhooks.query.ts";
import {
  ApiWebhook,
  ApiWebhookEventType,
  getWebhookEventTypeTitleKey,
} from "@/types/webhook.types.ts";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Avatar,
  Button,
  DropdownMenu,
  DropdownMenuItem,
  FontAwesomeIcon,
  Input,
  ListItem,
  Modal,
  ModalProps,
  Multiselect,
  Stack,
  Textarea,
  Title,
  useModals,
} from "@skailer-com/uikit";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";

interface WebhookEditModalProps extends ModalProps {
  webhook?: ApiWebhook;
}

interface WebhookFormValues {
  title: string;
  description: string;
  url: string;
  event_types: ApiWebhookEventType[];
}

export function WebhookEditModal({ webhook, ...props }: WebhookEditModalProps) {
  const { close, openModal } = useModals();
  const { t } = useTranslation();
  const schema = z.object({
    title: z.string().trim().min(1, t("errors:field_required")),
    description: z.string(),
    url: z.string().min(1, t("errors:field_required")).url(t("core:webhook_url_invalid")),
    event_types: z.array(z.enum(ApiWebhookEventType)).min(1, t("core:webhook_event_required")),
  });

  const {
    control,
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<WebhookFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: webhook?.title ?? "",
      description: webhook?.description ?? "",
      url: webhook?.url ?? "",
      event_types: webhook?.event_types ?? [],
    },
  });
  const saveWebhook = useWebhookSave(control);
  const [headers, setHeaders] = useState<WebhookHeaderValue[]>(() =>
    Object.entries(webhook?.headers ?? {}).map(([key, value]) => ({ key, value }))
  );

  const eventOptions = Object.values(ApiWebhookEventType).map((eventType) => ({
    id: eventType,
    title: t(getWebhookEventTypeTitleKey(eventType)),
  }));

  const addHeader = () => {
    openModal(WebhookHeaderEditModal, {
      existingKeys: headers.map((header) => header.key),
      onSave: (header) => setHeaders((current) => [...current, header]),
    });
  };

  const editHeader = (index: number) => {
    openModal(WebhookHeaderEditModal, {
      header: headers[index],
      existingKeys: headers.filter((_, itemIndex) => itemIndex !== index).map((item) => item.key),
      onSave: (header) =>
        setHeaders((current) =>
          current.map((item, itemIndex) => (itemIndex === index ? header : item))
        ),
    });
  };

  const onSubmit = handleSubmit(async (data) => {
    try {
      const result = await saveWebhook.mutateAsync({
        id: webhook?.id,
        payload: {
          ...data,
          title: data.title.trim(),
          description: data.description.trim() || null,
          headers: Object.fromEntries(headers.map((header) => [header.key, header.value])),
          ...(webhook ? {} : { is_active: true }),
        },
      });
      close(props.id);
      if ("signature_secret" in result && typeof result.signature_secret === "string") {
        openModal(WebhookSecretModal, { secret: result.signature_secret });
      }
    } catch {
      return;
    }
  });

  return (
    <Modal
      {...props}
      size={"lg"}
      title={webhook ? t("core:webhook_edit") : t("core:webhook_create")}
      withCloser
      actions={
        <Stack gap={"sm"}>
          <ServerErrorField errors={errors} />
          <Button size={"lg"} onClick={onSubmit} isLoading={saveWebhook.isPending}>
            {t("common:save")}
          </Button>
        </Stack>
      }
    >
      <form onSubmit={onSubmit}>
        <Input
          {...register("title")}
          label={t("common:title")}
          autoFocus
          required
          error={errors.title?.message}
        />
        <Textarea
          {...register("description")}
          label={t("common:description")}
          error={errors.description?.message}
        />
        <Input
          {...register("url")}
          label={t("common:url")}
          placeholder={"https://example.com/webhooks"}
          required
          error={errors.url?.message}
        />

        <Controller
          control={control}
          name={"event_types"}
          render={({ field }) => (
            <Multiselect
              label={t("core:webhook_event_types")}
              emptyTitle={t("common:select")}
              isMulti
              searchable
              required
              options={eventOptions}
              values={field.value}
              onChange={field.onChange}
              error={errors.event_types?.message}
            />
          )}
        />

        <Stack gap={"sm"}>
          <Title header={t("core:webhook_custom_headers")} size={"xs"} paddingTop paddingBottom />
          {headers.map((header, index) => (
            <ListItem
              key={`${header.key}-${index}`}
              behavior={"card"}
              icon={<Avatar icon={<FontAwesomeIcon icon={"fa-light fa-brackets-curly"} />} />}
              preTitle={header.key}
              title={header.value}
              valueSlot={
                <DropdownMenu>
                  <DropdownMenuItem
                    title={t("common:edit")}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-pen"} />}
                    onClick={() => editHeader(index)}
                  />
                  <DropdownMenuItem
                    title={t("common:delete")}
                    leftIcon={<FontAwesomeIcon icon={"fa-light fa-trash"} />}
                    variant={"menuDanger"}
                    onClick={() => setHeaders((current) => current.filter((_, i) => i !== index))}
                  />
                </DropdownMenu>
              }
            />
          ))}
          <Stack direction={"horizontal"}>
            <Button type={"button"} variant={"secondary"} size={"sm"} onClick={addHeader}>
              {t("workflow:action_type_http_request_add_header")}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Modal>
  );
}
